import { type Color as ColorType } from '@backend/lib/db/typeRoots/color'

type HEX = `#${string}`
type RGB = `rgb(${number}, ${number}, ${number})`
type RGBA = `rgba(${number}, ${number}, ${number}, ${number})`

type ColorInit =
  | Prettify<Omit<ColorType, 'a' | 'index'> & Partial<Pick<ColorType, 'a'>>>
  | HEX
  | RGB
  | RGBA

export const Color = (init: ColorInit): ColorType => {
  if (typeof init === 'string') {
    if (init.startsWith('#')) {
      return hexToColor(init as HEX)
    } else {
      return rgbToColor(init as RGB | RGBA)
    }
  } else if (typeof init === 'object') {
    return {
      r: init.r,
      g: init.g,
      b: init.b,
      a: init.a ?? 255,
    }
  } else {
    throw new Error(`Invalid input: ${init}`)
  }
}

export const colorToString = (color: ColorType, type: 'rgba' | 'hex' = 'hex'): RGBA | HEX => {
  const { r, g, b, a } = color
  const clamp = (v: number) => Math.min(Math.max(v, 0), 255)

  if (type === 'hex') {
    const toHexDigit = (n: number) => Math.round(clamp(n)).toString(16).padStart(2, '0')
    return `#${toHexDigit(r)}${toHexDigit(g)}${toHexDigit(b)}${toHexDigit(a)}`
  }

  const precision = 1000
  const alpha = Math.round((clamp(a) / 255) * precision) / precision
  return `rgba(${Math.round(clamp(r))}, ${Math.round(clamp(g))}, ${Math.round(clamp(b))}, ${alpha})`
}

/**
 * @param rgbString rgb(255, 255, 255) | rgba(255, 255, 255, 1)
 * @returns a Color object
 */
export const rgbToColor = (rgbString: string): ColorType => {
  if (!rgbString.startsWith('rgb(') && !rgbString.startsWith('rgba(')) {
    throw new Error(`Invalid rgb / rgba color: ${rgbString}`)
  }

  let type: 'rgb' | 'rgba'
  let _rgbString
  if (rgbString.startsWith('rgb(')) {
    _rgbString = rgbString.slice(4, -1)
    type = 'rgb'
  } else {
    _rgbString = rgbString.slice(5, -1)
    type = 'rgba'
  }

  if (_rgbString.match(/[^0-9,.\s]/)) {
    throw new Error(`Invalid rgb / rgba color: ${rgbString}`)
  }

  const values = _rgbString.split(',').map((s) => s.trim())

  const toCheck = type === 'rgb' ? values.slice() : values.slice(0, -1)
  for (const v of toCheck) {
    if (v.match(/[^0-9,\s]/)) {
      throw new Error(`Invalid rgb / rgba color: ${rgbString}`)
    }
  }

  if ((type === 'rgb' && values.length !== 3) || (type === 'rgba' && values.length !== 4)) {
    throw new Error(`Invalid rgb / rgba color: ${rgbString}`)
  }

  return {
    r: parseInt(values[0]!),
    g: parseInt(values[1]!),
    b: parseInt(values[2]!),
    a: values.length === 4 ? Math.round(parseFloat(values[3]!) * 255) : 255,
  }
}

/**
 *
 * @param hex #rgb | #rgba | #rrggbb | #rrggbbaa
 * @returns a Color object
 */
export const hexToColor = (hex: string): ColorType => {
  if (!hex.startsWith('#')) {
    throw new Error(`Invalid hex color: ${hex}`)
  }

  const _hex = hex.slice(1)

  if (_hex.match(/[^0-9a-fA-F]/)) {
    throw new Error(`Invalid hex color: ${hex}`)
  }

  let result: string[] = []

  switch (_hex.length) {
    case 3:
    case 4:
      result = (_hex.match(/.{1}/g) || []).map((char) => char + char)
      break

    case 6:
    case 8:
      result = _hex.match(/.{2}/g) || []
      break

    default:
      throw new Error(`Invalid hex color: ${hex}`)
  }

  if (result.length !== 3 && result.length !== 4) {
    throw new Error(`Error parsing hex color: ${hex}`)
  }

  return {
    r: parseInt(result[0]!, 16),
    g: parseInt(result[1]!, 16),
    b: parseInt(result[2]!, 16),
    a: result.length === 4 ? parseInt(result[3]!, 16) : 255,
  }
}

export const colorToHex = (color: ColorType): string => {
  const r = Math.round(color.r).toString(16).padStart(2, '0')
  const g = Math.round(color.g).toString(16).padStart(2, '0')
  const b = Math.round(color.b).toString(16).padStart(2, '0')
  const a = Math.round(color.a).toString(16).padStart(2, '0')

  return `#${r}${g}${b}${a}`
}
